import type { ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'

export interface MetricTileProps {
  label: string
  value: ReactNode
  /** Qué mide exactamente. Va siempre: un número sin alcance no se puede leer. */
  hint: string
  icon?: ReactNode
  /** Token de color para el número, cuando el número ES el estado. */
  tone?: 'neutral' | 'overdue' | 'done'
  className?: string
}

const TONE: Record<NonNullable<MetricTileProps['tone']>, string | undefined> = {
  neutral: undefined,
  overdue: '--overdue',
  done: '--phase-done',
}

/**
 * Un número grande con su etiqueta y su alcance.
 *
 * El `hint` no es decoración: es la línea que evita la pregunta "¿esto cuenta
 * los pausados?". El QA funcional encontró dos números correctos a treinta
 * centímetros uno del otro que parecían contradecirse sólo porque ninguno
 * declaraba qué contaba.
 */
export function MetricTile({
  label,
  value,
  hint,
  icon,
  tone = 'neutral',
  className,
}: MetricTileProps) {
  const token = TONE[tone]

  return (
    <div
      className={cn(
        'hairline flex min-w-[150px] flex-1 flex-col gap-0.5 rounded-[var(--radius-card)] bg-surface-1 px-3 py-2.5',
        className,
      )}
    >
      <span className="flex items-center gap-1.5 text-2xs text-text-2">
        {icon}
        {label}
      </span>
      <span
        className="num text-xl leading-none"
        style={token ? { color: `hsl(var(${token}))` } : undefined}
      >
        {value}
      </span>
      <span className="text-2xs text-text-3">{hint}</span>
    </div>
  )
}
